<template>
    <aside class="ds-settings-aside">
        <section class="ds-settings-card ds-settings-changes" aria-labelledby="settings-changes-title" :aria-busy="editor.saving.value || undefined">
            <header class="ds-settings-changes-header">
                <h2 id="settings-changes-title" class="ds-settings-card-title">Изменения</h2>
                <span v-if="changes.length" class="ds-settings-badge">{{ changes.length }}</span>
            </header>
            <p v-if="isRoles && editor.selectedRole.value" class="ds-settings-changes-subtitle">
                Роль «{{ editor.selectedRole.value.name }}»
            </p>

            <!-- Неизвестный результат записи: повтор заблокирован до сверки. -->
            <div v-if="showGlobalReload" class="ds-settings-changes-alert" role="alert">
                <p>Результат сохранения неизвестен: настройки не перечитаны.</p>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="editor.load.global === 'loading'" @click="editor.loadGlobal()">
                    Повторить загрузку
                </button>
            </div>
            <div v-if="showLunchReload" class="ds-settings-changes-alert" role="alert">
                <p>Список обеденных правил не перечитан после ошибки. Повтор сохранения обедов недоступен до загрузки.</p>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="editor.load.lunches === 'loading'" @click="editor.loadLunches()">
                    Повторить загрузку
                </button>
            </div>
            <div v-if="showLunchVerify" class="ds-settings-changes-alert" role="alert">
                <p>Результат создания правила неизвестен. Проверьте список обеденных перерывов: лишние правила удалите, затем подтвердите сверку.</p>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="editor.confirmLunchVerification()">
                    Список проверен
                </button>
            </div>

            <ul v-if="result?.errors.length" class="ds-settings-changes-errors" role="alert">
                <li v-for="error in result.errors" :key="error.id + error.message">
                    <span class="ds-settings-changes-error-block">{{ error.block }}:</span> {{ error.message }}
                </li>
            </ul>
            <ul v-if="result?.warnings.length" class="ds-settings-changes-warnings" role="status">
                <li v-for="warning in result.warnings" :key="warning">{{ warning }}</li>
            </ul>

            <div v-if="groups.length" class="ds-settings-changes-list">
                <section v-for="group in groups" :key="group.block" class="ds-settings-changes-group">
                    <h3 class="ds-settings-changes-group-title">{{ group.block }}</h3>
                    <div v-for="change in group.items" :key="change.id" class="ds-settings-change">
                        <div class="ds-settings-change-text">
                            <button
                                v-if="change.anchor"
                                type="button"
                                class="ds-settings-change-label"
                                @click="editor.revealAnchor(change.anchor)"
                            >{{ change.label }}</button>
                            <div v-else class="ds-settings-change-label">{{ change.label }}</div>
                            <div v-if="change.techKey" class="ds-settings-change-key">{{ change.techKey }}</div>
                            <div class="ds-settings-change-values">
                                {{ change.before }} → <b>{{ change.after }}</b>
                            </div>
                            <div v-if="change.invalid" class="ds-settings-change-invalid">Исправьте значение</div>
                            <div v-if="change.revertBlockedReason" class="ds-settings-change-note">{{ change.revertBlockedReason }}</div>
                        </div>
                        <button
                            type="button"
                            class="ds-settings-icon-button"
                            :disabled="editor.saving.value || Boolean(change.revertBlockedReason)"
                            :aria-label="`Отменить изменение: ${change.label}`"
                            :title="change.revertBlockedReason || 'Отменить изменение'"
                            @click="editor.revertChange(change)"
                        >
                            <SettingsIcon name="undo" :size="16" />
                        </button>
                    </div>
                </section>
            </div>

            <div v-else-if="!result?.errors.length" class="ds-settings-changes-empty">
                <SettingsIcon name="check" :size="22" />
                <div class="ds-settings-changes-empty-title">Всё сохранено</div>
                <div class="ds-settings-changes-empty-text">Изменения появятся здесь по мере правки</div>
            </div>

            <p v-if="editor.currentInvalid.value" class="ds-settings-changes-hint">Сохранение недоступно: есть некорректные значения.</p>

            <footer class="ds-settings-changes-actions">
                <button
                    type="button"
                    class="ds-glass-btn ds-glass-btn-ghost"
                    :disabled="!changes.length || editor.saving.value"
                    @click="editor.resetCurrent()"
                >{{ isRoles ? 'Сбросить' : 'Сбросить всё' }}</button>
                <button
                    type="button"
                    class="ds-glass-btn ds-glass-btn-primary"
                    :disabled="!editor.canSaveCurrent.value"
                    @click="editor.saveCurrent()"
                >{{ editor.saving.value ? 'Сохранение…' : isRoles ? 'Сохранить роль' : 'Сохранить' }}</button>
            </footer>
        </section>
    </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SettingsIcon from './SettingsIcon.vue'
import { useSettingsEditor } from './useSettingsEditor'

const editor = useSettingsEditor()

const isRoles = computed(() => editor.section.value === 'roles')
const changes = computed(() => editor.currentChanges.value)
const groups = computed(() => editor.currentChangeGroups.value)
const result = computed(() => editor.currentResult.value)

const usesGlobal = computed(() => editor.section.value === 'general' || editor.section.value === 'exchange')
const showGlobalReload = computed(() => usesGlobal.value && editor.globalNeedsReload.value)
const showLunchReload = computed(() => editor.section.value === 'general' && editor.lunchBlocked.value === 'reload')
const showLunchVerify = computed(() => editor.section.value === 'general' && editor.lunchBlocked.value === 'verify')
</script>
